import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, Megaphone, BarChart, Search } from 'lucide-react';
import Navbar from '../../layout/Navbar';
import Footer from '../../layout/Footer';
import { Link } from 'react-router-dom';

const DigitalMarketing = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll('[data-animate]');

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach(observer => observer.disconnect());
    }, []);

    const services = [
        { name: 'SEO', desc: 'Improve search rankings and organic traffic.' },
        { name: 'PPC Advertising', desc: 'Targeted ads for immediate results.' },
        { name: 'Social Media Marketing', desc: 'Engage audiences on platforms like Facebook and Instagram.' },
        { name: 'Content Marketing', desc: 'Create valuable content to attract customers.' },
        { name: 'Email Marketing', desc: 'Nurture leads with personalized campaigns.' },
        { name: 'Analytics & Reporting', desc: 'Data-driven insights for optimization.' },
    ];

    const strategies = [
        { name: "Content Strategy", desc: "Tailored content plans for brand growth." },
        { name: "SEO Audit", desc: "Comprehensive site optimizations." },
        { name: "Social Media Management", desc: "Daily engagement and growth." },
        { name: "Paid Media", desc: "Efficient ad spend management." },
        { name: "Conversion Optimization", desc: "A/B testing for better results." },
        { name: "Influencer Partnerships", desc: "Collaborate for wider reach." },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div
                        className="animate-fade-in-up"
                        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                    >
                        <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                            Digital
                            <br />
                            <span className="text-green-500 inline-block animate-pulse">Marketing</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                            Driving <span className="text-green-500 font-semibold">growth through strategic digital campaigns</span> that deliver measurable results.
                        </p>
                    </div>

                    {/* Scroll Indicator */}
                    <div className="animate-bounce mt-16">
                        <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gray-900" data-animate>
                <div
                    className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                        }`}
                >
                    <div className="text-center text-white">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-green-400">
                            OneHexa Tech
                        </h2>
                        <p className="text-lg text-gray-300">
                            OneHexa Tech can also provide digital marketing services tailored to your requirements.
                        </p>
                    </div>
                </div>
            </section>

            {/* Service Overview Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Our Digital <span className="text-green-500">Marketing</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
                            Comprehensive strategies using <span className="text-green-500 font-semibold">SEO, PPC, and social media</span> to elevate your brand's online presence.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Approach */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Megaphone className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Approach</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Customized campaigns focused on <span className="text-green-500 font-semibold">ROI and audience targeting</span> for maximum impact.
                            </p>
                        </div>

                        {/* Benefits */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <BarChart className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Key Benefits</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Higher visibility, more leads, and <span className="text-green-500 font-semibold">sustainable growth</span> for your business.
                            </p>
                        </div>

                        {/* Process */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <ArrowRight className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Process</h3>
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Audit & Strategy</li>
                                <li>Execution & Monitoring</li>
                                <li>Analysis & Optimization</li>
                                <li>Reporting & Scaling</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-animate>
                <div className={`max-w-7xl mx-auto text-center transition-all duration-1000 delay-250 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        Our <span className="text-green-500">Services</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
                        A full suite of <span className="text-green-500 font-semibold">digital marketing services</span> to propel your brand.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-gray-50 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border hover:border-green-300">
                                <Search className="w-10 h-10 text-green-500 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{service.name}</h3>
                                <p className="text-gray-600">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Strategies Section with Show More */}
            <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Marketing <span className="text-green-500">Strategies</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Proven tactics to achieve your marketing goals.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 cursor-pointer">
                        {(showMore ? strategies : strategies.slice(0, 6)).map((strategy, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200"
                            >
                                <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-green-600 transition-colors duration-300">{strategy.name}</h3>
                                <p className="text-sm text-gray-600">{strategy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Amplify Your <span className="text-green-400">Brand</span>
                    </h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Let OneHexa Tech handle your digital marketing needs.
                    </p>
                    <Link to="/contact">
                        <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                            Start Campaign
                        </button>
                    </Link>
                </div>
            </section>

            <Footer />

            <style jsx>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 1s ease-out;
                }
            `}</style>
        </div>
    );
};

export default DigitalMarketing;